'use client';

import { useMemo, useState } from 'react';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { CityPageQueryQuery } from '@medprev-web-app/api-graphql-client';

import { getServiceChannelFlags } from '@/app/(main)/(pages)/_utils/business-model.util';
import { Typography } from '@/components/ui/typography/typography.component';
import { ContentContainer } from '@/components/ui/content-container.component';
import { MostSearchedResult, MostSearchedResultType, SearchType } from '@/types/most-search-type';
import { ProceduresCard } from '@/app/(main)/(pages)/_components/most-searched-section/default/procedures-card.component';
import { ProceduresFilter } from '@/app/(main)/(pages)/_components/most-searched-section/default/procedures-filter.component';
import { cardDisplayMostSearched } from '@/app/(main)/(pages)/(home)/_consts/card-display-most-searched.consts';
import { useIsGreaterThan } from '@/hooks/use-is-greather-than.hook';
import { MostSearchedLoading } from '@/app/(main)/(pages)/(home)/_components/home-loading/most-searched-loading.component';
import { Place } from '@/types/google-maps.types';
import { useMostSearchedProcedures } from '@/hooks/use-most-searched.hook';
import { handleOpenSearchModal } from '@/store/search/handle-open-search-modal';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

interface MostSearchedSectionProps {
  cityConfig?: CityPageQueryQuery;
  procedures?: MostSearchedResultType[];
  initialFilter?: MostSearchedType;
  hasLoading?: boolean;
  locationOverride?: Pick<Place, 'city' | 'state'>;
}

const defaultTerms = {
  [SearchType.PRESENTIAL_CONSULTATION]: 'consulta',
  [SearchType.ONLINE_CONSULTATION]: 'teleconsulta',
  [SearchType.LABORATORIAL_EXAM]: 'exame',
  [SearchType.IMAGE_EXAM]: 'exame de imagem',
};

export type MostSearchedType = keyof typeof defaultTerms;

export const MostSearchedSection = ({
  hasLoading = true,
  locationOverride,
  cityConfig,
}: MostSearchedSectionProps) => {
  const {
    hasMedprevOnline,
  } = getServiceChannelFlags(cityConfig);
  const analytics = getAnalyticsPlugin();

  const titleSection = cityConfig && !hasMedprevOnline
    ? 'Teleconsultas mais procuradas'
    : locationOverride
      ? `Mais procurados em ${locationOverride.city} - ${locationOverride.state}`
      : 'Mais procurados';
  const isSingleCard = !useIsGreaterThan('xl');
  const { groups: procedures, isLoading: isLoadingProcedures } = useMostSearchedProcedures();

  const isLoading = hasLoading && isLoadingProcedures;

  const [selectedFilter, setFilter] = useState<MostSearchedType>(
    cityConfig && !hasMedprevOnline
      ? SearchType.ONLINE_CONSULTATION
      : SearchType.PRESENTIAL_CONSULTATION,
  );

  const [cardOne, cardTwo] = useMemo(() => {
    const list = procedures?.find((item) => item.type === selectedFilter);
    const cards = cardDisplayMostSearched({ isSingleCard, selectedProcedureList: list?.procedures ?? [] });

    return [cards[0], cards[1]];
  }, [selectedFilter, procedures, isSingleCard]);

  const goToSearchProcedureSchedules = (mostSearchedResultOption: MostSearchedResult): void => {
    analytics.track(AnalyticsEnum.CLICK_MOST_SEARCHED_PROCEDURE, {
      procedimento: mostSearchedResultOption?.name?.toLocaleLowerCase(),
      procedimento_label: mostSearchedResultOption?.displayName?.toLocaleLowerCase(),
      categoria: selectedFilter.toLocaleLowerCase(),
      categoria_label: defaultTerms[selectedFilter],
    });
    handleOpenSearchModal({ searchTerm: mostSearchedResultOption.displayName });
  };

  const onSeeMore = (): void => {
    analytics.track(AnalyticsEnum.CLICK_MOST_SEARCHED_SEE_MORE);
    handleOpenSearchModal({ searchTerm: defaultTerms[selectedFilter as MostSearchedType] });
  };

  const handleChangeFilter = (filter: MostSearchedType) => {
    setFilter(filter);
    analytics.track(AnalyticsEnum.CLICK_MOST_SEARCHED_CATEGORY, {
      categoria: filter.toLocaleLowerCase(),
      categoria_label: defaultTerms[filter],
    });
  };

  if (isLoading) {
    return <MostSearchedLoading />;
  }

  return (
    <section
      data-testid={DataTestIdEnum.CITY_MOST_SEARCHED_SECTION}
      className="w-full flex flex-col justify-center items-center"
    >
      <ContentContainer className="flex flex-col px-0 overflow-hidden sm:overflow-visible sm:px-0 md:max-w-full xl:mb-6">
        <Typography className="mb-2 mt-6 px-6 sm:px-16 md:mx-auto md:max-w-xl md:w-full md:px-0 xl:w-full xl:max-w-none font-semibold">
          {titleSection}
        </Typography>

        {(!cityConfig || hasMedprevOnline) && (
          <ProceduresFilter
            onFilterSelect={handleChangeFilter}
            selectedFilter={selectedFilter}
            procedures={procedures}
          />
        )}

        <div className="flex gap-6 w-full">
          <div className="w-full xl:w-[31.25rem] 3xl:w-[40.625rem]">
            <ProceduresCard
              procedures={cardOne}
              onSeeMore={isSingleCard ? onSeeMore : undefined}
              goToSearchProcedureSchedules={goToSearchProcedureSchedules}
            />
          </div>
          {!isSingleCard && (
            <div className="w-full xl:w-[31.25rem] 3xl:w-[40.625rem]">
              <ProceduresCard
                procedures={cardTwo}
                onSeeMore={onSeeMore}
                goToSearchProcedureSchedules={goToSearchProcedureSchedules}
              />
            </div>
          )}
        </div>
      </ContentContainer>
    </section>
  );
};
